import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { ImgButton, ImgButtonWithText, LayoutUtils, Mesh2D, MeshUtils, Rectangle3D, } from "@dreamcam/babylon-tools";
import { Env } from "../../../settings/Env";
import { DcRenderingGroup } from "../../DcRenderingGroup";
import { MeshName } from "../../MeshName";
import { text } from "../../../TextResources";
import { XRInputHelper } from "../../XRInputHelper";
import { TypedEventEmitter } from "@dreamcam/web-sdk";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder.js";
import { Mesh } from "@babylonjs/core/Meshes/mesh.js";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture.js";
import { Texture } from "@babylonjs/core/Materials/Textures/texture.js";
export class BaseSlideShowPanel extends TypedEventEmitter {
    set CurrentSlide(idx) {
        if (this._currentSlide === idx)
            return;
        this._currentSlide = idx;
        this._updateSlides();
    }
    get CurrentSlide() {
        return this._currentSlide;
    }
    constructor(parentNode) {
        super();
        this._isPrepared = false;
        this._slides = [];
        this._currentSlide = 0;
        this._parentNode = parentNode;
    }
    async prepareAsync() {
        if (this._isPrepared)
            return;
        const env = Env.lobby.onboarding;
        const scene = this._parentNode.getScene();
        this._mesh = MeshUtils.createPlaneMesh(MeshName.onboardingMesh, env.width, env.height, scene, this._parentNode, DcRenderingGroup.aboveDialogs);
        this._blockMesh = MeshBuilder.CreateCylinder(MeshName.onboardingBlockerMesh, { height: 3, diameter: 2.8, cap: Mesh.NO_CAP, hasRings: true, }, scene);
        this._blockMesh.parent = this._parentNode;
        this._blockMesh.position = new Vector3(0, 0, 0);
        this._blockMesh.renderingGroupId = DcRenderingGroup.dialogs;
        // this._blockMesh.material = new StandardMaterial(MeshName.onboardingBlockerMesh + "Material");
        // this._blockMesh.material.backFaceCulling = false;
        this._mesh.mesh.position = new Vector3(env.position.x, env.position.y, env.position.z);
        this._mesh.mesh.rotateAround(Vector3.Zero(), Vector3.Right(), env.rotation / 180 * Math.PI);
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh.mesh);
        pointerAddon.onPointerOut.add(() => this._mesh?.notifyChildrenOnPointerOut());
        Mesh2D.Meshes.filter(m => m.name !== MeshName.onboardingMesh && m.name !== MeshName.onboardingBlockerMesh).forEach(m => m.mesh.colorMultiplier = 0.4);
        MeshUtils.hide(this._mesh);
        this._blockMesh.isVisible = false;
        // This GUI container and its pointer blocker prevent pointer hover on other meshes:
        const advancedDynamicTexture = new AdvancedDynamicTexture(MeshName.onboardingBlockerMesh + " AdvancedDynamicTexture", 1, 1, scene, false, Texture.TRILINEAR_SAMPLINGMODE);
        advancedDynamicTexture.attachToMesh(this._blockMesh);
        const block = new Rectangle3D("onboardingBlockRectangle");
        block.width = 1;
        block.height = 1;
        block.thickness = 0;
        block.background = env.blockScreen.background;
        block.isPointerBlocker = true;
        advancedDynamicTexture.addControl(block);
        const backgroundPanel = new Rectangle3D("onboardingBackground");
        backgroundPanel.widthInPixels = this._mesh.m2px(env.width);
        backgroundPanel.heightInPixels = this._mesh.m2px(env.height);
        // backgroundPanel.paddingTopInPixels = 1; // Babylon-js bug workaround.
        backgroundPanel.cornerRadius = this._mesh.m2px(env.cornerRadius);
        backgroundPanel.background = env.backgroundColor;
        backgroundPanel.thickness = 0;
        this._mesh.texture.addControl(backgroundPanel);
        const texts = await this._getSlideTextsAsync();
        const layouts = this._getSlideLayouts(texts.length);
        for (let i = 0; i < texts.length; i++) {
            const slide = this._createSlide(i, texts.length, layouts[i], texts[i], this._mesh, {
                next: () => { this.CurrentSlide++; },
                back: () => { this.CurrentSlide--; },
                close: () => { this._close(); },
            });
            backgroundPanel.addControl(slide);
            this._slides.push(slide);
        }
        this._updateSlides();
        this._isPrepared = true;
    }
    _setVisibility(isVisible) {
        if (!this._isPrepared)
            return;
        if (this._mesh)
            MeshUtils.setVisibility(this._mesh, isVisible);
        if (this._blockMesh)
            this._blockMesh.isVisible = isVisible;
        //MeshUtils.setVisibility(this._blockMesh, isVisible);
        this.emit("onVisibilityChanged", isVisible);
    }
    show() {
        this._setVisibility(true);
    }
    hide() {
        this._setVisibility(false);
    }
    _close() {
        this.dispose();
        this.emit("onClose");
    }
    _updateSlides() {
        if (this.CurrentSlide >= this._slides.length || this.CurrentSlide < 0)
            this._close();
        for (let i = 0; i < this._slides.length; i++) {
            const slide = this._slides[i];
            slide.isVisible = i === this.CurrentSlide;
        }
    }
    dispose() {
        if (!this._isPrepared)
            return;
        this._setVisibility(false);
        Mesh2D.Meshes.filter(m => m.name !== MeshName.onboardingMesh && m.name !== MeshName.onboardingBlockerMesh).forEach(m => m.mesh.colorMultiplier = 1);
        this._slides.forEach(s => s.dispose());
        this._slides.length = 0;
        this._mesh?.dispose();
        this._mesh = undefined;
        this._blockMesh?.dispose();
        this._blockMesh = undefined;
        this._isPrepared = false;
    }
}
export class SlideBase extends Container {
    constructor(_position, _totalSlides, _layout, _content, mesh, func) {
        super();
        this._mesh = mesh;
        this._func = func;
    }
    AddCloseButton() {
        const env = Env.lobby.onboarding;
        const closeButton = new ImgButton("onboardingButtonClose", env.closeButton);
        closeButton.heightInPixels = this._mesh.m2px(env.closeButton.height + env.closeButton.top);
        closeButton.widthInPixels = this._mesh.m2px(env.closeButton.width + env.closeButton.right);
        closeButton.paddingRightInPixels = this._mesh.m2px(env.closeButton.right);
        closeButton.paddingTopInPixels = this._mesh.m2px(env.closeButton.top);
        closeButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        closeButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        closeButton.onClickObserver.add(() => {
            this._func.close();
        });
        this.addControl(closeButton);
    }
    AddBackButton() {
        const env = Env.lobby.onboarding;
        const backButton = new ImgButton("onboardingButtonBack", env.backButton);
        backButton.heightInPixels = this._mesh.m2px(env.backButton.height + env.backButton.top);
        backButton.widthInPixels = this._mesh.m2px(env.backButton.width + env.backButton.left);
        backButton.paddingLeftInPixels = this._mesh.m2px(env.backButton.left);
        backButton.paddingTopInPixels = this._mesh.m2px(env.backButton.top);
        backButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        backButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        backButton.onClickObserver.add(() => {
            this._func.back();
        });
        this.addControl(backButton);
    }
    AddNextButton(buttonCaption) {
        const env = Env.lobby.onboarding;
        const nextButton = new ImgButtonWithText("onboardingButtonNext", {
            text: buttonCaption === undefined ? text.onboarding_nextButton : buttonCaption,
            textLayout: this._mesh.textLayoutM2Px(env.nextButton.textLayout),
            imageLayout: LayoutUtils.GetButtonLayout(env.nextButton.imageLayout, this._mesh),
        });
        nextButton.heightInPixels = this._mesh.m2px(env.nextButton.height + env.nextButton.bottom);
        nextButton.widthInPixels = this._mesh.m2px(env.nextButton.width);
        nextButton.paddingBottomInPixels = this._mesh.m2px(env.nextButton.bottom);
        nextButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        nextButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        nextButton.onClickObserver.add(() => {
            this._func.next();
        });
        this.addControl(nextButton);
    }
}
/*
class SlideHelper {
    public static Create<TLayout extends BasicSlideLayout, TContent extends SlideContent>(
        position: number,
        totalSlides: number,
        layout: TLayout,
        content: TContent,
        mesh: Mesh2D,
        func: SlideFunctions): SlideBase {

        if (position === 0)
            return new OnboardingFirstSlide(position, totalSlides, layout as unknown as FirstOnboardingSlideLayout, content as unknown as FirstOnboardingSlideContent, mesh, func);
        if (position === totalSlides - 1)
            return new OnboardingLastSlide(position, totalSlides, layout as unknown as LastOnboardingSlideLayout, content as unknown as LastOnboardingSlideContent, mesh, func);
        return new OnboardingSlide(position, totalSlides, layout as unknown as RegularOnboardingSlideLayout, content, mesh, func);
    }
}
*/
